import type { NextPageContext } from 'next';
import type { ParsedUrlQueryInput } from 'querystring';
import { type UrlObject } from 'url';

import {
  NEXT_PUBLIC_DEFAULT_APP_LOCALE,
  PLACEHOLDER_COUNTRY_SLUG,
  PLACEHOLDER_LANGUAGE_SLUG,
  PLACEHOLDER_SF_ID_SLUG,
} from '@/lib/const';
import { getLocaleFromPath, isSupportedLocale } from '@/lib/locales/locales';

import {
  getActionAriaLabelSync,
  getAriaLabelSync,
  loadAriaLabels,
} from './ariaLabels';

export const externalLinkReg = /^(http|https):\/\//;
export const parseUrlOrigin = (url: string) => {
  try {
    const urlObj = new URL(url);
    return urlObj.origin;
  } catch (e) {
    return url;
  }
};
export const getUrlText = (link: string) => {
  if (externalLinkReg.test(link)) return link;
  return link.startsWith('/') ? link : `/${link}`;
};

export const asPathOrEmpty = (asPath?: string | null): string => asPath ?? '';

const splitAsPath = (asPath?: string | null): [string, string] => {
  const [pathname = '', searchString = ''] = asPathOrEmpty(asPath).split('?');
  return [pathname, searchString];
};

export const getPathnameFromAsPath = (asPath?: string | null): string =>
  splitAsPath(asPath)[0];

export const getSearchStringFromAsPath = (asPath?: string | null): string =>
  splitAsPath(asPath)[1];

export const extractPathFromUrl = (url?: string | null) => {
  const safeUrl = asPathOrEmpty(url);

  if (!externalLinkReg.test(safeUrl)) {
    return getPathnameFromAsPath(safeUrl);
  }

  try {
    return new URL(safeUrl).pathname;
  } catch {
    return getPathnameFromAsPath(safeUrl);
  }
};

export function removeNxtPQueryParams(url?: string | null): string {
  const [path, queryString] = splitAsPath(url);

  if (!queryString) {
    return path;
  }

  const searchParams = new URLSearchParams(queryString);

  const filteredParams = Array.from(searchParams.entries()).filter(
    ([key]) => !key.startsWith('nxtP'),
  );

  const remainingQueryString = new URLSearchParams(filteredParams).toString();

  return remainingQueryString ? `${path}?${remainingQueryString}` : path;
}

export function extractPageUrlFromNextContext(
  ctx: NextPageContext,
  domain: string,
) {
  let currentUrl = ctx.asPath
    ? `${removeTrailingSlash(domain)}${ctx.asPath}`
    : '';
  if (ctx.locale && !ctx.asPath?.includes(ctx.locale)) {
    currentUrl = ctx.asPath ? `${domain}${ctx.locale}${ctx.asPath}` : '';
  }

  // If last path segment contains a dot, do not keep a trailing slash
  if (currentUrl) {
    const [path, suffix = ''] = currentUrl.split(/([?#].*)/);
    const segments = path.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1] || '';
    if (lastSegment.includes('.')) {
      currentUrl = path.replace(/\/$/, '') + suffix;
    }
  }

  return removeNxtPQueryParams(currentUrl);
}

export function removeTrailingSlash(uri: string): string {
  uri = uri.replace(/%2f$/gi, '');
  uri = uri.replace(/\/$/g, '');
  return uri;
}

export function addTrailingSlash(url: string): string {
  const [path, suffix = ''] = url.split(/([?#].*)/);

  if (path === '' || path === '/') {
    return url;
  }
  const normalizedPath = path.endsWith('/') ? path : path + '/';

  return normalizedPath + suffix;
}

export function isValidBreitlingDomain(src: string): boolean {
  try {
    const url = new URL(src);
    return (
      url.hostname.endsWith('.breitling.com') ||
      url.hostname === 'breitling.com'
    );
  } catch (error) {
    return false;
  }
}

export function injectCountrySlugAndLanguageSlugIntoUrl({
  countrySlug,
  languageSlug,
  salesForceId,
  url,
}: {
  countrySlug: string;
  languageSlug: string;
  salesForceId?: string;
  url: string;
}): string {
  return url
    .replaceAll(PLACEHOLDER_COUNTRY_SLUG, countrySlug)
    .replaceAll(PLACEHOLDER_LANGUAGE_SLUG, languageSlug)
    .replaceAll(PLACEHOLDER_SF_ID_SLUG, salesForceId ?? '');
}

export const getLinkAriaLabelAsync = async (
  url: UrlObject | string,
  languageCode: string,
): Promise<string | undefined> => {
  await loadAriaLabels(languageCode);
  return getAriaLabelSync(url, languageCode);
};

export const getLinkAriaLabel = (
  url: UrlObject | string,
  languageCode = 'EN_US',
) => {
  return getAriaLabelSync(url, languageCode);
};

export const getActionAriaLabel = (
  actionKey: string,
  languageCode = 'en-US',
) => {
  return getActionAriaLabelSync(actionKey, languageCode);
};

const resolveEnsureLocale = (locale: string): string =>
  isSupportedLocale(locale) ? locale : NEXT_PUBLIC_DEFAULT_APP_LOCALE;

export const ensureLocale = (
  url: string | UrlObject,
  locale: string,
): string | UrlObject => {
  if (typeof url === 'string') {
    const trimmedUrl = url.trim();
    const isExternalLink =
      trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://');
    const isSpecialLink =
      trimmedUrl.startsWith('tel:') || trimmedUrl.startsWith('mailto:');

    const finalLocale = resolveEnsureLocale(
      getLocaleFromPath(url) ?? locale ?? NEXT_PUBLIC_DEFAULT_APP_LOCALE,
    );
    const [countrySlug, localeSlug] = finalLocale.split('-');
    const urlWithCountryAndLanguage = injectCountrySlugAndLanguageSlugIntoUrl({
      url: trimmedUrl,
      countrySlug: countrySlug.toUpperCase(),
      languageSlug: localeSlug.toLowerCase(),
    });

    return urlWithCountryAndLanguage.startsWith(`/${finalLocale}`) ||
      isSpecialLink ||
      isExternalLink
      ? urlWithCountryAndLanguage
      : `/${finalLocale}${
          urlWithCountryAndLanguage.startsWith('/')
            ? urlWithCountryAndLanguage
            : `/${urlWithCountryAndLanguage}`
        }`;
  } else {
    const finalLocale = resolveEnsureLocale(
      getLocaleFromPath(url.pathname ?? '') ??
        locale ??
        NEXT_PUBLIC_DEFAULT_APP_LOCALE,
    );
    return {
      ...url,
      pathname: url.pathname
        ? url.pathname.startsWith(`/${finalLocale}`)
          ? url.pathname
          : `/${finalLocale}${url.pathname}`
        : `/${finalLocale}`,
    };
  }
};

/**
 * Checks if pathname matches the target path (ignoring trailing slash differences)
 * @param pathname - The current pathname
 * @param targetPath - The target path to compare against
 * @returns True if paths match, false otherwise
 */
export function isPathMatch(
  pathname: string | null | undefined,
  targetPath: string | null | undefined,
): boolean {
  if (
    pathname === null ||
    pathname === undefined ||
    targetPath === null ||
    targetPath === undefined
  ) {
    return false;
  }
  return removeTrailingSlash(pathname) === removeTrailingSlash(targetPath);
}

function isValidPathParam(value: unknown): value is string | number | boolean {
  return ['string', 'number', 'boolean'].includes(typeof value);
}

// Replace param in pathname with the actual value eg: /us-en/sunglasses/[modelSku]/ -> /us-en/sunglasses/cloudbreak-0004-2-JZ310610.25/
export function buildAbsolutePageUrl(
  target: UrlObject | string,
  locale: string,
  baseUrl: string = process.env.NEXT_PUBLIC_URL ?? '',
): string {
  if (typeof target === 'string' && externalLinkReg.test(target)) {
    return target;
  }

  const localizedTarget = ensureLocale(target, locale);
  const path =
    typeof localizedTarget === 'string'
      ? getUrlText(localizedTarget)
      : buildResolvedPath(
          localizedTarget.pathname ?? '',
          localizedTarget.query,
        );

  if (!baseUrl) {
    return addTrailingSlashUnlessFile(path);
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const absoluteUrl = new URL(normalizedPath, baseUrl).toString();

  return addTrailingSlashUnlessFile(absoluteUrl);
}

export const buildResolvedPath = (
  pathname: string,
  query: string | ParsedUrlQueryInput | null | undefined,
): string => {
  if (!pathname) return '';

  let resolvedPath = pathname;
  const searchParams = new URLSearchParams();

  if (query && typeof query === 'object') {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;

      const paramPlaceholder = `[${key}]`;

      // Handle path parameter replacement
      if (resolvedPath.includes(paramPlaceholder) && isValidPathParam(value)) {
        resolvedPath = resolvedPath.replace(paramPlaceholder, String(value));
        continue;
      }

      // Handle query parameters
      if (Array.isArray(value)) {
        value.forEach((v) => v !== undefined && searchParams.append(key, v));
      } else {
        searchParams.append(key, value as string);
      }
    }

    const queryString = searchParams.toString();
    if (queryString) {
      resolvedPath += `?${queryString}`;
    }
  }

  return resolvedPath;
};

export function getIntParam(param: string): number {
  if (!param) return 0;

  try {
    const raw = decodeURIComponent(param); // handle %20, %3A, etc.
    const digits = raw.match(/\d+/)?.[0]; // extract first number sequence
    return digits ? parseInt(digits, 10) : 0;
  } catch {
    return 0;
  }
}

export function addTrailingSlashUnlessFile(url: string): string {
  const [path] = url.split(/([?#].*)/);
  if (path === '' || path === '/') return url;
  try {
    const parsed = new URL(path, 'http://dummy');
    const segments = parsed.pathname.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1] || '';
    if (lastSegment.includes('.')) {
      return url;
    }
  } catch {
    const segments = path.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1] || '';
    if (lastSegment.includes('.')) {
      return url;
    }
  }
  return addTrailingSlash(url);
}

/**
 * Extracts the query string portion from a Next.js `router.asPath` value.
 * Returns an empty string when there is no query string.
 */
export const getSearchFromPath = (asPath?: string | null): string => {
  const searchString = getSearchStringFromAsPath(asPath);
  return searchString ? `?${searchString}` : '';
};

export const parseBooleanQueryParam = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (Array.isArray(value)) return parseBooleanQueryParam(value[0]);
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return (
      normalized === 'true' ||
      normalized === '1' ||
      normalized === 'yes' ||
      normalized === 'on'
    );
  }
  if (typeof value === 'number') return value === 1;
  return false;
};
